import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import { OrgReportsApi, OrgVideoViewReportRow } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

/**
 * Video View Report — org tier, read-only. Cross-school watch stats for
 * real YouTube lesson/topic videos, matching the Departments/Academic
 * Calendar/Finance report pattern. Class/Subject are per-school lookups
 * (classes/subjects live in each school's own schema), so both are
 * disabled until a specific school is picked — same cascade as the
 * Academic Calendar/Lessons reports. Subject options are derived from the
 * loaded (school+class-filtered) rows themselves rather than a separate
 * lookup endpoint, since the report already returns subjectId/subjectName
 * per row. Real watch data is extremely concentrated (confirmed live:
 * only 4 real lessons, 2 students, 1 school have any trackable activity
 * platform-wide) — most rows legitimately show 0 views / N/A duration.
 * That's accurate, not a bug, same as school-tier.
 */
export function OrgVideoViewReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-video-view-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['org-video-view-report-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });

  const paramsForSubjectOptions = { schoolId: schoolId || undefined, classId: (schoolId && classId) || undefined };

  const { data: unfilteredRows = [], isFetching: subjectsLoading } = useQuery({
    queryKey: ['org-video-view-report-subject-options', paramsForSubjectOptions],
    queryFn: () => OrgReportsApi.videoViewReport(paramsForSubjectOptions),
    enabled: !!schoolId && !!classId,
  });
  const subjects = useMemo(() => {
    const seen = new Map<string, { subjectId: string; subjectName: string }>();
    for (const r of unfilteredRows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, { subjectId: r.subjectId, subjectName: r.subjectName });
    return [...seen.values()].sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  }, [unfilteredRows]);

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setClassId('');
    setSubjectId('');
  }
  function handleClassChange(v: string) {
    setClassId(v);
    setSubjectId('');
  }

  const params = {
    schoolId: schoolId || undefined,
    classId: (schoolId && classId) || undefined,
    subjectId: (schoolId && classId && subjectId) || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-video-view-report', params],
    queryFn: () => OrgReportsApi.videoViewReport(params),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportVideoViewReport(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Video View Report"
        description="Watch stats for real YouTube videos attached to lessons and topics, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-44"
          value={classId}
          onChange={(e) => handleClassChange(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by class'}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-44"
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
          disabled={!classId || subjectsLoading}
          title={classId ? undefined : 'Select a class to filter by subject'}
        >
          <option value="">All Subjects</option>
          {subjects.map((s) => (
            <option key={s.subjectId} value={s.subjectId}>
              {s.subjectName}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Lesson</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Topic</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Video Name</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Views</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Duration</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Watched Duration</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Watched %</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Last Watched</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Applicable Students</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-slate-400">
                    No YouTube videos found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: OrgVideoViewReportRow) => (
                  <tr key={`${r.schoolId}:${r.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.schoolName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.subjectName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.lessonName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.topicName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{r.videoName}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge tone={r.totalViews > 0 ? 'blue' : 'slate'}>{r.totalViews}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.totalDuration ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.watchedDuration ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.watchedPercentage ?? 0}%</td>
                    <td className="px-4 py-3 text-slate-700">{r.lastWatched ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.applicableStudents}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
